"use client";

/**
 * Keeps the global modal stack in step with browser history.
 *
 * Responsibilities (all of them are Back/Forward correctness, not deep-linking
 * convenience):
 *
 *  1. **Back closes the modal.** `useHubModalController` pushes a history entry
 *     when it opens a URL-configured modal, so Back removes the `?modal=` param
 *     — this effect sees that and closes the modal instead of leaving it open.
 *  2. **Forward re-opens it**, and a cold deep link opens it (hydration).
 *  3. **Nested modals unwind one at a time** — Back from `edit` returns to
 *     `detail`, not all the way out.
 *  4. **A route change never leaves a modal behind.** The stack provider lives
 *     in the root layout, so without this a modal opened on /contacts would
 *     still be open after navigating to /companies (the "stuck modal" bug).
 *  5. **Closing via ESC / X / click-away unwinds the history entry** we pushed,
 *     so the user isn't left with a Back that re-opens what they just closed.
 *
 * ## Only reconcile against a SETTLED url
 *
 * Every rule above is a reaction to the URL, and the URL lags the click that
 * caused it — `router.push`/`back()` commit on a later tick. Reacting to a URL
 * while one of our own navigations is still in flight means reacting to a URL
 * that has already been superseded, which is precisely how "close a modal,
 * immediately click another row" ended with the new modal being closed again:
 * the close's `router.back()` landed after the new modal had opened, and rule
 * (1) dutifully applied it.
 *
 * So the controller records the chain of navigations it has asked for, and
 * `classifyObservedUrl` sorts every URL this effect observes into:
 *
 *  - `settled`     → our latest request arrived. Reconcile against it.
 *  - `stale`       → a request we have since superseded finally landed. Ignore
 *                    it entirely; it describes a past we have already left.
 *  - `superseding` → not ours, so the user navigated. That outranks anything we
 *                    have pending: drop the chain and reconcile.
 */

import { useEffect, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useGlobalModalStack } from "@wtc/ui";
import {
  buildCleanUrl,
  parseHubModalUrlState,
} from "../url/modal-url-config";
import { reconcileModalHistory } from "./modal-history-reconcile";
import {
  beginNavigation,
  classifyObservedUrl,
  clearPendingNavigation,
  useModalHistoryStore,
} from "./modal-history-store";

export function useModalHistorySync(): void {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const store = useModalHistoryStore();
  const { openModal, closeModal, isModalOpen, modalStack } =
    useGlobalModalStack();

  const previousPathnameRef = useRef<string | null>(null);
  const searchParamsKey = searchParams?.toString() ?? "";

  useEffect(() => {
    const safePathname = pathname ?? "";
    const currentUrl = `${safePathname}${searchParamsKey ? `?${searchParamsKey}` : ""}`;
    const parsed = parseHubModalUrlState(searchParams);
    const urlModalId = parsed?.modalId ?? null;

    // (4) Route change — nothing survives it except a modal the new URL names.
    // Our modal navigations never change the pathname, so a pathname change is
    // always someone else's and always outranks anything we have pending.
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = safePathname;
    const routeChanged =
      previousPathname !== null && previousPathname !== safePathname;
    if (routeChanged) {
      store.entries = [];
      clearPendingNavigation(store);
      const openIds = Object.keys(modalStack.state).filter(
        (id) => modalStack.state[id] && id !== urlModalId
      );
      for (const id of openIds) {
        closeModal(id);
      }
    }

    // Settle gate. See the module comment: act only on a URL that is ours and
    // has arrived, or one the user produced. Never on a superseded one.
    if (classifyObservedUrl(store, currentUrl) === "stale") return;
    clearPendingNavigation(store);

    // (1)(2)(3) Reconcile what we track against what the URL says.
    const { toClose, toOpen, nextEntries } = reconcileModalHistory(
      store.entries,
      urlModalId,
      parsed?.entityId ?? null
    );
    store.entries = nextEntries;
    for (const id of toClose) {
      closeModal(id);
    }
    if (toOpen && parsed) {
      openModal(toOpen, parsed.params);
    }

    // (5) The URL still names the innermost modal but the stack says it is
    // closed → the user closed it through the modal's own UI. Unwind the
    // history entry we created so Back doesn't resurrect it.
    const innermost = store.entries[store.entries.length - 1];
    if (!innermost || innermost.modalId !== urlModalId) return;

    if (isModalOpen(innermost.modalId)) {
      innermost.observedOpen = true;
      return;
    }
    if (!innermost.observedOpen) return;

    store.entries = store.entries.slice(0, -1);
    if (innermost.pushed && innermost.urlBefore !== null) {
      beginNavigation(store, innermost.urlBefore, currentUrl);
      router.back();
      return;
    }
    const cleanUrl = buildCleanUrl(
      safePathname,
      searchParams,
      innermost.modalId
    );
    if (cleanUrl !== currentUrl) {
      beginNavigation(store, cleanUrl, currentUrl);
      router.replace(cleanUrl);
    }
    // `modalStack` is read (`.state`) but intentionally not a dependency: the
    // stack-derived callbacks below (`isModalOpen`/`openModal`/`closeModal`)
    // already change identity on every modal toggle, so the effect re-runs
    // whenever stack state moves — which is exactly what step (5) needs to
    // notice an ESC/X close. Adding `modalStack` too would only duplicate that.
  }, [
    pathname,
    searchParamsKey,
    searchParams,
    router,
    store,
    openModal,
    closeModal,
    isModalOpen,
  ]);
}
